import type { ReactElement } from "react";
import { useState, useCallback, useMemo, useRef, useLayoutEffect } from "react";
import { FullscreenTakeover, Card, Text, TextInput, Flex } from "@jsq/ds/core";
import { TreeComponent } from "~/components/TreeComponent";
import { useGetClientEntityHierarchyQuery } from "~/graphql/supergraphHooks";
import { useBillingDate } from "~/pages/Billing/contexts/BillingDateContext";
import { HierarchyNodeCard } from "./HierarchyNodeCard";
import {
  takeoverBody,
  hierarchyCard,
  hierarchyHeader,
  searchContainer,
  scrollableContent,
  loadingContainer,
} from "./styles_vanilla.css";
import { sortBillingGroups, isNotAssignedGroup } from "./utils";
import type { BillingGroupData, EntityData } from "./types";
import type { TreeNodeData } from "~/components/TreeComponent/types";

type ClientEntityHierarchyTakeoverProps = {
  clientId: string;
  clientName: string;
  onExit: () => void;
};

const mapEntities = (
  entities: ReadonlyArray<{
    entityId: number;
    entityName: string;
    productLine: string;
    eumValue: number | null;
    fundFamilyTrace: ReadonlyArray<string> | null;
  }>,
): EntityData[] =>
  entities.map((e) => ({
    entityId: e.entityId,
    entityName: e.entityName,
    productLine: e.productLine,
    eumValue: e.eumValue,
    fundFamilyTrace: e.fundFamilyTrace ? [...e.fundFamilyTrace] : null,
  }));

const mapBillingGroups = (
  billingGroups: ReadonlyArray<{
    billingGroupId: string | null;
    billingGroupName: string | null;
    entities: ReadonlyArray<{
      entityId: number;
      entityName: string;
      productLine: string;
      eumValue: number | null;
      fundFamilyTrace: ReadonlyArray<string> | null;
    }>;
  }>,
): BillingGroupData[] =>
  billingGroups.map((g) => ({
    billingGroupId: g.billingGroupId,
    billingGroupName: g.billingGroupName,
    entities: mapEntities(g.entities),
  }));

export const ClientEntityHierarchyTakeover = ({
  clientId,
  clientName,
  onExit,
}: ClientEntityHierarchyTakeoverProps): ReactElement => {
  const { asOfDateString } = useBillingDate();
  const [searchQuery, setSearchQuery] = useState("");
  const [isClientExpanded, setIsClientExpanded] = useState(true);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const treeContainerRef = useRef<HTMLDivElement>(null);
  const [treeBounds, setTreeBounds] = useState<DOMRect>();

  useLayoutEffect(() => {
    const measureBounds = () => {
      if (treeContainerRef.current) {
        const newBounds = treeContainerRef.current.getBoundingClientRect();
        setTreeBounds((prev) =>
          !prev ||
          Math.abs(prev.width - newBounds.width) > 1 ||
          Math.abs(prev.height - newBounds.height) > 1
            ? newBounds
            : prev,
        );
      }
    };

    measureBounds();
    const timeoutId = setTimeout(measureBounds, 1000);
    return () => clearTimeout(timeoutId);
  }, []);

  const { loading, data, error } = useGetClientEntityHierarchyQuery({
    variables: { clientId, asOfDate: asOfDateString },
    fetchPolicy: "no-cache",
  });

  const billingGroups = useMemo((): BillingGroupData[] => {
    if (!data?.getClientEntityHierarchy?.billingGroups) return [];
    return sortBillingGroups(mapBillingGroups(data.getClientEntityHierarchy.billingGroups));
  }, [data]);

  const filteredBillingGroups = useMemo((): BillingGroupData[] => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return billingGroups;

    return billingGroups
      .map((group) => {
        const matchingEntities = group.entities.filter((entity) =>
          entity.entityName.toLowerCase().includes(query),
        );

        if (matchingEntities.length > 0) {
          return { ...group, entities: matchingEntities };
        }

        if (group.billingGroupName?.toLowerCase().includes(query)) {
          return group;
        }

        return null;
      })
      .filter((g): g is BillingGroupData => g !== null);
  }, [billingGroups, searchQuery]);

  const softwareEumUsd = data?.getClientEntityHierarchy?.softwareEumUsd ?? null;
  const billableFaEumUsd = data?.getClientEntityHierarchy?.billableFaEumUsd ?? null;

  const handleToggleGroup = useCallback((groupId: string | null) => {
    const key = groupId ?? "__not_assigned__";
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }, []);

  const groupEum = useCallback(
    (group: BillingGroupData): number =>
      group.entities.reduce((sum, e) => sum + (e.eumValue ?? 0), 0),
    [],
  );

  const buildTreeNode = useCallback(
    (group: BillingGroupData): TreeNodeData => {
      const notAssigned = isNotAssignedGroup(group);
      const groupKey = group.billingGroupId ?? "__not_assigned__";
      const isExpanded = expandedGroups.has(groupKey);
      const hasChildren = group.entities.length > 0;

      const entityNodes: TreeNodeData[] | undefined =
        isExpanded && hasChildren
          ? group.entities.map((entity) => ({
              id: `entity-${entity.entityId}`,
              content: (
                <HierarchyNodeCard
                  nodeType="entity"
                  entity={entity}
                  isNotAssigned={notAssigned}
                />
              ),
              parentId: `bg-${groupKey}`,
            }))
          : undefined;

      return {
        id: `bg-${groupKey}`,
        content: (
          <HierarchyNodeCard
            nodeType="billingGroup"
            billingGroupName={group.billingGroupName ?? "Not Assigned"}
            billingGroupEum={groupEum(group)}
            entityCount={group.entities.length}
            isNotAssigned={notAssigned}
            isExpanded={isExpanded}
            onToggle={() => handleToggleGroup(group.billingGroupId)}
          />
        ),
        parentId: `client-${clientId}`,
        leafNodes: entityNodes,
      };
    },
    [clientId, expandedGroups, groupEum, handleToggleGroup],
  );

  const treeData = useMemo((): TreeNodeData | null => {
    if (!data) return null;

    const billingGroupNodes = isClientExpanded ? filteredBillingGroups.map(buildTreeNode) : [];
    const hasChildren = filteredBillingGroups.length > 0;

    return {
      id: `client-${clientId}`,
      content: (
        <HierarchyNodeCard
          nodeType="client"
          clientName={clientName}
          softwareEumUsd={softwareEumUsd}
          billableFaEumUsd={billableFaEumUsd}
          hasChildren={hasChildren}
          isExpanded={isClientExpanded}
          onToggle={() => setIsClientExpanded((prev) => !prev)}
        />
      ),
      leafNodes: billingGroupNodes.length > 0 ? billingGroupNodes : undefined,
    };
  }, [
    data,
    filteredBillingGroups,
    buildTreeNode,
    clientId,
    clientName,
    softwareEumUsd,
    billableFaEumUsd,
    isClientExpanded,
  ]);

  return (
    <FullscreenTakeover>
      <FullscreenTakeover.Header
        onExit={onExit}
        title={`Client to entity hierarchy · ${clientName}`}
      />
      <FullscreenTakeover.Body className={takeoverBody}>
        <Card className={hierarchyCard}>
          <Text variant="h3" className={hierarchyHeader}>
            {clientName} client to entity hierarchy
          </Text>

          <div className={searchContainer}>
            <TextInput
              placeholder="Search for an entity"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              icon="search"
            />
          </div>

          <div className={scrollableContent} ref={treeContainerRef}>
            {loading && !data && (
              <Flex justifyContent="center" alignItems="center" className={loadingContainer}>
                <Text variant="secondary-md">Loading...</Text>
              </Flex>
            )}

            {error && !data && (
              <Flex justifyContent="center" alignItems="center" className={loadingContainer}>
                <Text variant="secondary-md">Failed to load hierarchy. Please try again.</Text>
              </Flex>
            )}

            {treeData && (
              <TreeComponent data={treeData} treeConfig={{ nodeMaxWidth: treeBounds?.width }} />
            )}
          </div>
        </Card>
      </FullscreenTakeover.Body>
    </FullscreenTakeover>
  );
};
